import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useSearchParams, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useCurrency } from '../../contexts/CurrencyContext';
import NavigationHeader from '../../components/ui/NavigationHeader';
import Button from '../../components/ui/Button';
import Icon from '../../components/AppIcon';
import PropertyCard from './components/PropertyCard';
import SubscriptionBanner from './components/SubscriptionBanner';
import QuickAIModal from './components/QuickAIModal';
import PaywallModal from '../../components/ui/PaywallModal';
import { stripeService } from '../../services/stripe';
import { PLAN_SLUGS, getPlanConfig } from '../../config/plans';
import propertyService from '../../services/propertyService';
import usageTrackingService from '../../services/usageTracking';
import { calculateMortgagePayment, calculateMortgageBalance, getPaymentsMadeAfterYears } from '../../utils/mortgageCalculations';

const MainDashboard = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const { user, userProfile, updateProfile, refreshProfile } = useAuth();
  const { formatCurrency } = useCurrency();

  const [currentTier, setCurrentTier] = useState(PLAN_SLUGS.TRIAL);
  const [subscription, setSubscription] = useState(null);
  const [loadingSubscription, setLoadingSubscription] = useState(true);
  const [showSuccessMessage, setShowSuccessMessage] = useState(false);
  const isUpdatingProfileRef = useRef(false);

  const [properties, setProperties] = useState([]);
  const [loadingProperties, setLoadingProperties] = useState(true);
  const [propertiesError, setPropertiesError] = useState('');

  const [showQuickAIModal, setShowQuickAIModal] = useState(false);
  const [processingQuickAI, setProcessingQuickAI] = useState(false);

  const [showPaywall, setShowPaywall] = useState(false);
  const [usageStats, setUsageStats] = useState(null);
  const [paywallFeature, setPaywallFeature] = useState('new_analysis');

  // Sync tier from profile (normalize to new model)
  useEffect(() => {
    if (userProfile?.subscription_tier) {
      setCurrentTier((prevTier) => {
        const newTier = stripeService.normalizeTier(userProfile.subscription_tier);
        return prevTier !== newTier ? newTier : prevTier;
      });
    } else if (userProfile) {
      setCurrentTier(PLAN_SLUGS.TRIAL);
    }
  }, [userProfile?.subscription_tier, userProfile]);

  useEffect(() => {
    const ensureTrialPlan = async () => {
      if (!user?.id || !userProfile) return;
      if (userProfile.subscription_tier) return;
      await stripeService.assignTrialPlan(user.id);
      await refreshProfile?.();
    };
    ensureTrialPlan();
  }, [user?.id, userProfile, refreshProfile]);

  useEffect(() => {
    const loadSubscription = async () => {
      if (!user?.id) {
        setLoadingSubscription(false);
        return;
      }

      if (isUpdatingProfileRef.current) return;

      setLoadingSubscription(true);
      try {
        const { data } = await stripeService?.getUserSubscription(user?.id);
        if (data) setSubscription(data);
      } catch (error) {
        console.error('Error loading subscription:', error);
      } finally {
        setLoadingSubscription(false);
      }
    };

    loadSubscription();
  }, [user?.id]);

  useEffect(() => {
    const sessionId = searchParams?.get('session_id');
    if (sessionId && user?.id) {
      setShowSuccessMessage(true);

      const reloadData = async () => {
        try {
          const { data } = await stripeService?.getUserSubscription(user?.id);
          if (data) {
            setSubscription(data);
            if (data?.tier) {
              await updateProfile({ subscription_tier: data?.tier });
              await refreshProfile();
            }
          }
        } catch (error) {
          console.error('Error reloading subscription:', error);
        }
      };

      reloadData();
      window.history?.replaceState({}, '', '/main-dashboard');
      setTimeout(() => setShowSuccessMessage(false), 5000);
    }
  }, [searchParams?.get('session_id'), user?.id, updateProfile, refreshProfile, searchParams]);

  useEffect(() => {
    const loadProperties = async () => {
      if (!user?.id) {
        setLoadingProperties(false);
        return;
      }

      setLoadingProperties(true);
      try {
        const result = await propertyService?.getUserProperties(user?.id);

        if (!result?.success) {
          throw new Error(result?.error || 'Erreur lors du chargement');
        }

        setProperties(result?.data || []);
      } catch (error) {
        console.error('Error loading properties:', error);
        setPropertiesError(error?.message || 'Erreur lors du chargement des propriétés');
      } finally {
        setLoadingProperties(false);
      }
    };

    loadProperties();
  }, [user?.id]);

  useEffect(() => {
    const loadUsageStats = async () => {
      if (!user?.id) return;
      try {
        const stats = await usageTrackingService.getUsageStats(user.id);
        setUsageStats(stats);
      } catch (error) {
        console.error('Error loading usage stats:', error);
      }
    };
    loadUsageStats();
  }, [user?.id, properties]); // reload when properties change (delete/create)

  const paywallAutoOpenedRef = useRef(false);

  useEffect(() => {
    if (location.state?.openPaywall && !paywallAutoOpenedRef.current) {
      paywallAutoOpenedRef.current = true;

      // Priorité: feature, sinon reason, sinon new_analysis
      const nextFeature = location.state?.feature || location.state?.reason || 'new_analysis';
      setPaywallFeature(nextFeature);
      setShowPaywall(true);

      // Nettoie le state pour éviter ré-ouverture au refresh / back
      navigate(location.pathname, { replace: true, state: {} });
    }
  }, [location.state, location.pathname, navigate]);

  const calculatePropertyKPIs = (property) => {
    const monthlyCashflow = property?.monthly_cashflow || 0;
    const noi = property?.noi || 0;
    const totalInvestment =
      property?.total_initial_investment || property?.down_payment_amount || 0;

    const annualCashflow = monthlyCashflow * 12;
    const cashOnCashReturn = totalInvestment > 0 ? (annualCashflow / totalInvestment) * 100 : 0;

    const mortgageAmountForDSCR = property?.mortgage_amount || 0;
    const interestRateForDSCR = property?.interest_rate || 0;
    const amortizationYearsForDSCR = property?.amortization_period || 25;
    const paymentFrequencyForDSCR = property?.payment_frequency || 'monthly';

    const mortgagePaymentCalc = calculateMortgagePayment(
      mortgageAmountForDSCR,
      interestRateForDSCR,
      amortizationYearsForDSCR,
      paymentFrequencyForDSCR
    );

    const annualMortgagePayment = mortgagePaymentCalc.annualPayment;
    const dscr = annualMortgagePayment > 0 ? noi / annualMortgagePayment : 0;

    // ROI total moyen (CAGR) sur 30 ans
    const projYears = 30;
    const purchasePrice = property?.purchase_price || 0;
    const appreciationRate = (property?.appreciation_rate || 3) / 100;
    const rentGrowth = (property?.rent_growth_rate || 2) / 100;
    const expenseGrowth = 0.02;
    const baseRevenue = (property?.total_monthly_revenue || 0) * 12;
    const baseExpenses = (property?.total_monthly_expenses || 0) * 12;

    let cumulativeCashflow = 0;
    for (let i = 0; i < projYears; i++) {
      const yRevenue = baseRevenue * Math.pow(1 + rentGrowth, i + 1);
      const yExpenses = baseExpenses * Math.pow(1 + expenseGrowth, i + 1);
      const yNOI = yRevenue - yExpenses;
      const yMortgage = (i + 1) > amortizationYearsForDSCR ? 0 : annualMortgagePayment;
      cumulativeCashflow += (yNOI - yMortgage);
    }
    const finalPropertyValue = purchasePrice * Math.pow(1 + appreciationRate, projYears);
    const valueAppreciation = finalPropertyValue - purchasePrice;
    const finalMortgageBalance = calculateMortgageBalance(
      mortgageAmountForDSCR, interestRateForDSCR, amortizationYearsForDSCR,
      paymentFrequencyForDSCR, getPaymentsMadeAfterYears(projYears, paymentFrequencyForDSCR)
    );
    const principalPaydown = mortgageAmountForDSCR - finalMortgageBalance;
    const totalROIAmount = cumulativeCashflow + valueAppreciation + principalPaydown;
    const totalROIDecimal = totalInvestment > 0 ? totalROIAmount / totalInvestment : 0;
    const avgAnnualROI = (1 + totalROIDecimal) > 0
      ? (Math.pow(1 + totalROIDecimal, 1 / projYears) - 1) * 100
      : 0;

    return { noi, cashOnCashReturn, dscr, avgAnnualROI };
  };

  const trialUsage = {
    used: Math.max(usageStats?.analyses?.used || 0, properties.length),
    limit: usageStats?.analyses?.limit || getPlanConfig(PLAN_SLUGS.TRIAL).analysisLimit,
  };
  const trialLimitReached =
    currentTier === PLAN_SLUGS.TRIAL
    && typeof trialUsage.limit === 'number'
    && trialUsage.used >= trialUsage.limit;

  const handleNewAnalysis = () => {
    if (trialLimitReached) {
      setPaywallFeature('new_analysis');
      setShowPaywall(true);
      return;
    }
    navigate('/property-analysis-wizard');
  };

  const handleQuickAI = async (params) => {
    if (!user?.id) {
      alert('Vous devez être connecté');
      return;
    }

    if (trialLimitReached) {
      setPaywallFeature('new_analysis');
      setShowPaywall(true);
      return;
    }

    setProcessingQuickAI(true);

    try {
      const importResult = await propertyService.analyzePropertyUrl(params.url, user.id);
      if (!importResult.success) throw new Error(importResult.error);

      const propertyData = importResult.data;
      const formData = {
        address: propertyData.address || '',
        propertyType: propertyData.propertyType || 'single_family',
        units: propertyData.units || '1',
        squareFeet: propertyData.squareFeet || '',
        yearBuilt: propertyData.yearBuilt || '',
        totalBedrooms: propertyData.totalBedrooms || '',
        purchasePrice: propertyData.purchasePrice || '',
        propertyUrl: params.url,
        downPaymentPercent: params.downPaymentPercent.toString(),
        interestRate: params.interestRate.toString(),
        amortizationPeriod: params.amortizationPeriod.toString(),
        paymentFrequency: params.paymentFrequency,
        closingCosts: (params.closingCosts || 0).toString(),
        schlPremium: params.schlPremium ? params.schlPremium.toString() : '',
        revenueInputType: propertyData.revenueInputType || 'monthly_per_unit',
        annualTotalRevenue: propertyData.annualTotalRevenue || '',
        totalMonthlyRevenue: propertyData.totalMonthlyRevenue || '',
        unitRevenues: propertyData.unitRevenues || [],
        propertyTaxes: propertyData.propertyTaxes || '',
        insurance: propertyData.insurance || '',
        electricity: propertyData.electricity || '',
        heating: propertyData.heating || '',
        water: propertyData.water || '',
        maintenance: propertyData.maintenance || '',
        otherExpenses: propertyData.otherExpenses || '',
        managementFeePercent: propertyData.managementFeePercent || '',
        notes: propertyData.notes || '',
        photos: propertyData.mainImageUrl
          ? [
              {
                url: propertyData.mainImageUrl,
                filePath: propertyData.imagePath || null,
                alt: propertyData.imageAlt || `Photo de ${propertyData.address}`,
              },
            ]
          : [],
      };

      const createResult = await propertyService.create(formData, user.id);
      if (!createResult.success) throw new Error(createResult.error);

      const propertyId = createResult.data.id;
      const metrics = propertyService.calculateMetrics(formData);
      const propertyDataForAI = { ...createResult.data, ...metrics };

      await propertyService.analyzePropertyAI(propertyDataForAI, user.id, propertyId, false);

      setShowQuickAIModal(false);
      navigate(`/analysis-results?propertyId=${propertyId}`);
    } catch (error) {
      console.error('Error in quick AI import:', error);
      alert(error?.message || 'Une erreur est survenue');
    } finally {
      setProcessingQuickAI(false);
    }
  };

  const handleStageChange = async (propertyId, newStage) => {
    if (!propertyId || !user?.id) return;

    try {
      const property = properties.find((p) => p.id === propertyId);
      if (!property) return;

      const currentFormData = propertyService.toFormFormat(property);
      const result = await propertyService.update(
        propertyId,
        { ...currentFormData, stage: newStage },
        user.id
      );

      if (result?.success) {
        const reloadResult = await propertyService?.getUserProperties(user?.id);
        if (reloadResult?.success) setProperties(reloadResult?.data || []);
      }
    } catch (error) {
      console.error('Error updating property stage:', error);
    }
  };

  const handleDeleteProperty = async (propertyId) => {
    if (!window.confirm('Supprimer cette propriété ?')) return;

    try {
      const result = await propertyService.delete(propertyId, user?.id);
      if (result?.success) {
        const reloadResult = await propertyService?.getUserProperties(user?.id);
        if (reloadResult?.success) setProperties(reloadResult?.data || []);
      }
    } catch (error) {
      console.error('Error deleting property:', error);
    }
  };

  // reprendre analyse en cours du landing page
  const pendingAnalysis = localStorage.getItem('pendingAnalysis');

  return (
    <div className="min-h-screen min-h-[100dvh] bg-background">
      <NavigationHeader
        user={{ name: user?.email?.split('@')[0] || 'Utilisateur' }}
        subscriptionTier={currentTier}
      />

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-24 pb-8">
        {/* Success Message */}
        {showSuccessMessage && (
          <div className="bg-success text-white p-4 rounded-lg mb-6 flex items-center gap-2">
            <Icon name="CheckCircle" size={20} />
            <span>Abonnement activé</span>
          </div>
        )}

        {/* Reprendre analyse */}
        {pendingAnalysis && (
          <div className="mb-6">
            <Button variant="primary" onClick={() => navigate('/property-analysis-wizard')}>
              Reprendre mon analyse
            </Button>
          </div>
        )}

        {/* Investor Profile Prompt - simplified */}
        {userProfile && !userProfile.investor_profile_completed && (
          <button
            onClick={() => navigate('/investor-profile')}
            className="w-full bg-muted border border-border rounded-xl p-4 mb-6 flex items-center gap-4 hover:bg-muted/80 transition-colors text-left"
          >
            <Icon name="User" size={24} color="var(--color-primary)" />
            <div className="flex-1">
              <p className="font-semibold text-[0.9375rem]">Personnalisez vos analyses</p>
              <p className="text-[0.8125rem] text-muted-foreground">3 questions rapides</p>
            </div>
            <Icon name="ChevronRight" size={20} color="var(--color-muted-foreground)" />
          </button>
        )}

        {/* Subscription Banner */}
        <div className="mb-6">
          <SubscriptionBanner currentTier={currentTier} usageStats={usageStats} />
        </div>

        {currentTier === PLAN_SLUGS.TRIAL && usageStats?.analyses && (
          <div className={`mb-6 rounded-xl border px-4 py-3 text-sm ${
            trialLimitReached
              ? 'border-destructive/30 bg-destructive/10 text-destructive'
              : 'border-primary/30 bg-primary/5 text-foreground'
          }`}>
            <strong>Essai gratuit :</strong>{' '}
            {usageStats.analyses.used}/{usageStats.analyses.limit} analyses utilisées.
            {trialLimitReached
              ? ' Passez à Analyse ou Pro pour créer une nouvelle analyse.'
              : ' Vous pouvez encore créer des analyses gratuites.'}
          </div>
        )}

        {/* Main CTA - New Analysis */}
        <div
          id="new-analysis"
          onClick={handleNewAnalysis}
          className="bg-primary rounded-2xl p-6 md:p-8 mb-8 cursor-pointer transform transition-all hover:scale-[1.02] relative"
        >
          <div className="flex items-center gap-4 md:gap-6">
            <div className="w-14 h-14 rounded-xl bg-white/20 flex items-center justify-center shrink-0">
              <Icon name="Plus" size={28} color="white" />
            </div>
            <div className="flex-1">
              <h2 className="text-xl font-bold text-white mb-1">Analyser une propriété</h2>
              <p className="text-sm text-white/80">Obtenez un verdict en quelques minutes</p>
            </div>
            <Icon name="ArrowRight" size={24} color="white" />
          </div>
        </div>

        {/* Quick AI Import — available for all tiers */}
        <button
          onClick={() => setShowQuickAIModal(true)}
          className="w-full bg-card border border-border rounded-xl p-4 mb-8 flex items-center gap-4 hover:bg-muted/50 transition-colors text-left"
          data-testid="quick-ai-import-btn"
        >
          <Icon name="Sparkles" size={20} color="var(--color-primary)" />
          <span className="text-[0.9375rem] text-foreground">Import rapide par URL</span>
          <Icon
            name="ChevronRight"
            size={18}
            color="var(--color-muted-foreground)"
            className="ml-auto"
          />
        </button>

        {/* Properties List */}
        <div id="property-list" className="relative">
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-lg font-semibold">Mes analyses</h3>
            {properties.length > 0 && (
              <button
                onClick={() => navigate('/mortgage-calculator')}
                className="text-primary text-sm flex items-center gap-1 hover:underline"
              >
                <Icon name="Calculator" size={16} />
                Calculateur hypothécaire
              </button>
            )}
          </div>

          {loadingProperties ? (
            <div className="text-center py-12">
              <Icon name="Loader2" size={32} className="animate-spin text-primary mx-auto" />
            </div>
          ) : propertiesError ? (
            <div className="text-center py-12 text-muted-foreground">{propertiesError}</div>
          ) : properties.length === 0 ? (
            <div className="bg-card border border-dashed border-border rounded-xl p-12 text-center">
              <Icon name="Building2" size={40} className="text-muted-foreground mb-4 mx-auto" />
              <p className="text-muted-foreground mb-4">Aucune propriété analysée</p>
              <Button variant="outline" size="sm" onClick={handleNewAnalysis}>
                Commencer une analyse
              </Button>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
             {properties.map((p) => (
  <PropertyCard
    key={p.id}
    property={{
      ...p,
      // IMPORTANT: donner au card le filePath si tu l'as
      imageFilePath:
        p?.photos?.[0]?.filePath ||
        p?.imagePath ||
        p?.mainImagePath ||
        null,

      // fallback URL si c’est déjà une URL (ex: import AI externe)
      imageUrl:
        p?.photos?.[0]?.url ||
        p?.mainImageUrl ||
        p?.image ||
        p?.mainImage ||
        null,

      imageAlt: p?.photos?.[0]?.alt || p?.mainImageAlt || `Photo de ${p.address}`,
    }}
    userProfile={userProfile}
    onClick={() => navigate(`/analysis-results?propertyId=${p.id}`)}
    onStageChange={(newStage) => handleStageChange(p.id, newStage)}
    onDelete={() => handleDeleteProperty(p.id)}
  />
))}
            </div>
          )}
        </div>
      </main>

      {/* Quick AI Modal */}
      <QuickAIModal
        isOpen={showQuickAIModal}
        onClose={() => setShowQuickAIModal(false)}
        onConfirm={handleQuickAI}
        subscriptionTier={currentTier}
        user={user}
        loading={processingQuickAI}
      />

      {/* Paywall Modal */}
      <PaywallModal
        isOpen={showPaywall}
        onClose={() => setShowPaywall(false)}
        feature={paywallFeature}
        usageStats={usageStats}
      />
    </div>
  );
};

export default MainDashboard;
